import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { fetchTeams } from '../utils/teams';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on mount by calling the API
    const checkAuth = async () => {
      try {
        // Try to get current user from API (will use httpOnly cookie)
        // CSRF token will be fetched automatically by interceptor if needed
        const response = await authAPI.getCurrentUser();
        setUser(response.data.user);
      } catch (error) {
        // User not authenticated or token expired
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    // Prefetch teams data for caching (doesn't block auth check)
    fetchTeams().catch((error) => {
      console.error('Failed to prefetch teams:', error);
    });

    checkAuth();
  }, []);

  const login = async (username, password) => {
    const response = await authAPI.login({ username, password });
    const { user } = response.data;
    setUser(user);
    return user;
  };

  const register = async (username, email, password) => {
    const response = await authAPI.register({ username, email, password });
    return response.data;
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const isAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'super_admin');
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAdmin,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
